import React from 'react';
import { TwoCol, KVRow, KVLabel, KVValue, Secondary, LaunchButton, SectionTitle } from './shared';
import EstimatedCostCard from './components/EstimatedCostCard';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { useAppSelector, useGenericPopup } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';

export interface ConfirmViewProps {
    onBack: () => void;
    onComplete?: () => void;
    templateTitle: string;
    productUrlCount?: number;
    retailerCount?: number;
    frequency?: 'daily' | 'weekly' | 'monthly';
    projectName?: string;
    startDate?: string;
    endDate?: string;
    urlsByRetailerId?: Record<string, string[]>;
}

const ConfirmView: React.FC<ConfirmViewProps> = ({ onBack, onComplete, templateTitle, productUrlCount = 0, retailerCount = 0, frequency = 'weekly', projectName, startDate, endDate, urlsByRetailerId }) => {
    const userEmail = useUserEmail() || '';
    const divisionId = useAppSelector((state) => _get(state, ['user', 'session', 'divisionId'], null));
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitStatus, setSubmitStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
    const defaultStatus = 'enabled';
    const { showPopup, closePopup } = useGenericPopup();
    const redirectRef = React.useRef(false);
    const timerRef = React.useRef<number | null>(null);
    const handleLaunch = async () => {
        try {
            setIsSubmitting(true);
            const payload = {
                name: projectName,
                templateType: templateTitle,
                frequency,
                startTime: startDate ?? null,
                endTime: endDate ?? null,
                createdBy: userEmail,
                retailerCount,
                divisionId: divisionId,
                status: defaultStatus,
                campaignContext: urlsByRetailerId || {},
            } as any;

            const path = buildLlmCrawlerPath('crawl-campaigns/create\?env\=dev');
            await axios.post(path, payload);
            setSubmitStatus('success');
            // Show confirmation popup
            showPopup({
                open: true,
                width: 300,
                height: 220,
                titleMessage: 'Launch successful',
                message: 'Your agent was created successfully.',
                onClose: () => {
                    if (redirectRef.current) return;
                    redirectRef.current = true;
                    if (onComplete) onComplete();
                }
            });
            if (onComplete) {
                timerRef.current = window.setTimeout(() => {
                    if (!redirectRef.current) {
                        redirectRef.current = true;
                        closePopup();
                        onComplete();
                    }
                }, 2500);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log('Failed to create agent', e);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    React.useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    return (
        <TwoCol style={{ width: 1045 }}>
            <div>
                <SectionTitle style={{ marginTop: 0, marginBottom: 8 }}>Summary</SectionTitle>
                <div style={{ width: '100%', height: 1, background: '#E6ECF2', marginBottom: 8 }} />
                <KVRow>
                    <KVLabel>Agent Name:</KVLabel>
                    <KVValue>{projectName || '[Untitled Project]'}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Template:</KVLabel>
                    <KVValue>{templateTitle}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Start Date:</KVLabel>
                    <KVValue>{startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not selected'}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>End Date:</KVLabel>
                    <KVValue>{endDate ? new Date(endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Not selected'}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Created By:</KVLabel>
                    <KVValue>{userEmail || '—'}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Created Date:</KVLabel>
                    <KVValue>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</KVValue>
                </KVRow>

                <div style={{ height: 16 }} />
                <SectionTitle style={{ marginTop: 0, marginBottom: 8 }}>Inputs</SectionTitle>
                <div style={{ width: '100%', height: 1, background: '#E6ECF2', marginBottom: 8 }} />
                <KVRow>
                    <KVLabel>Type:</KVLabel>
                    <KVValue>Product URLs</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Product URLs:</KVLabel>
                    <KVValue>{productUrlCount}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Retailers:</KVLabel>
                    <KVValue>{retailerCount}</KVValue>
                </KVRow>
                <KVRow>
                    <KVLabel>Frequency:</KVLabel>
                    <KVValue>{frequency.charAt(0).toUpperCase() + frequency.slice(1)}</KVValue>
                </KVRow>

                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
                    <Secondary onClick={onBack} style={{ borderRadius: 4, border: '1px solid #E8E8ED'}}>Back</Secondary>
                </div>
            </div>

            <div>
                <EstimatedCostCard
                    productUrls={productUrlCount}
                    retailers={retailerCount}
                    frequency={frequency ? (frequency.charAt(0).toUpperCase() + frequency.slice(1)) : '-'}
                />
                <div style={{ marginTop: 12 }}>
                    <LaunchButton onClick={handleLaunch} disabled={isSubmitting} style={{ width: '100%' }}>{isSubmitting ? 'Launching…' : 'Launch'}</LaunchButton>
                    {submitStatus !== 'idle' && (
                        <div style={{ marginTop: 12, fontSize: 12 }}>
                            {submitStatus === 'success' ? (
                                <div style={{ color: '#0B6B3A' }}>Campaign created. Redirecting to Summary…</div>
                            ) : (
                                <div style={{ color: '#9B1C1C' }}>Failed to create campaign. Please try again.</div>
                            )}
                            {submitStatus === 'error' && onComplete && (
                                <div style={{ marginTop: 8 }}>
                                    <Secondary onClick={onComplete}>Back to Summary</Secondary>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </TwoCol>
    );
}

export default ConfirmView;
